import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import './Report.css';
import { MigrationData, ReportAnalysisResponse } from '../types';
import { migrationApi } from '../services/migrationApi';

const CATEGORY_COLORS: Record<string, string> = {
  COBOL: '#ef4444', // Red
  JCL: '#f97316', // Orange
  COPYLIB: '#3b82f6', // Blue
  COPYBOOKS: '#3b82f6', // Alias for different naming
  DCLGLIB: '#10b981', // Green
  LOADLIB: '#a855f7' // Purple
};

const FALLBACK_COLORS = ['#8b5cf6', '#14b8a6', '#facc15', '#ec4899', '#0ea5e9'];

interface ReportLocationState {
  migration: MigrationData;
}

const Report: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMigration, setSelectedMigration] = useState<MigrationData | null>(null);
  const [reportData, setReportData] = useState<ReportAnalysisResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get migration data from location state
        const locationState = location.state as ReportLocationState | null;
        const migration = locationState?.migration;

        if (!migration) {
          setError('Migration data not found. Please navigate from the migration table.');
          setLoading(false);
          return;
        }

        setSelectedMigration(migration);

        const reportId = migration.report_id;

        if (!reportId) {
          setError('Report data is not available for this migration.');
          return;
        }

        // Fetch report analysis
        const analysis = await migrationApi.getReportAnalysis(reportId);
        if (!analysis) {
          setError('Report data is not available.');
          return;
        }
        setReportData(analysis);
      } catch (err: any) {
        console.error('Error fetching report data:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch report data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.state]);

  const handleBack = (): void => {
    navigate('/dashboard/migration');
  };

  const getCategoryColor = (name: string, index: number) => {
    const normalized = name.toUpperCase();
    return CATEGORY_COLORS[normalized] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  // Prepare pie chart data using dynamic summary keys
  const buildChartData = (valueKey: 'loc' | 'count') => {
    if (!reportData?.report_summary) return [];

    const entries = Object.entries(reportData.report_summary);
    const total = entries.reduce((sum, [, data]) => sum + (data?.[valueKey] || 0), 0);

    return entries.map(([name, data], index) => {
      const value = data?.[valueKey] || 0;
      return {
        name,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0,
        color: getCategoryColor(name, index)
      };
    });
  };

  const getLinesOfCodeData = () => buildChartData('loc');
  const getFilesByTypeData = () => buildChartData('count');

  const getTotalLinesOfCode = () => {
    if (!reportData?.report_summary) return 0;
    return Object.values(reportData.report_summary).reduce((sum, data) => sum + (data?.loc || 0), 0);
  };

  const getTotalFiles = () => {
    if (!reportData?.report_summary) return 0;
    return Object.values(reportData.report_summary).reduce((sum, data) => sum + (data?.count || 0), 0);
  };

  // Custom label function for pie chart that shows percentage for all segments
  const RADIAN = Math.PI / 180;
  const renderPercentageLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    payload
  }: any) => {
    if (percent === undefined) return '';

    const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const color = payload?.color || '#ffffff';

    return (
      <text
        x={x}
        y={y}
        fill={color}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {(percent * 100).toFixed(1)}%
      </text>
    );
  };

  const linesOfCodeData = getLinesOfCodeData();
  const filesByTypeData = getFilesByTypeData();
  const totalLines = getTotalLinesOfCode();
  const totalFiles = getTotalFiles();
  const legendCategories = reportData?.report_summary ? Object.keys(reportData.report_summary) : [];

  if (loading) {
    return (
      <div className="report-container">
        <div className="report-header">
          <button className="back-button" onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5m7-7l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Migrations
          </button>
          <h1 className="report-title">Migration Report</h1>
        </div>
        <div className="loading-container">
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-container">
        <div className="report-header">
          <button className="back-button" onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5m7-7l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Migrations
          </button>
          <h1 className="report-title">Migration Report</h1>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-container">
      {/* Header with back button */}
      <div className="report-header">
        <button className="back-button" onClick={handleBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5m7-7l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Migrations
        </button>
        <h1 className="report-title">Mainframe Code Analysis Dashboard</h1>
      </div>

      {/* Report Content */}
      <div className="report-content">
        {/* Migration Info Card */}
        <div className="migration-info-card">
          <h2 className="migration-title">Migration Details</h2>
          <div className="migration-details">
            <div className="detail-row">
              <span className="detail-label">Application:</span>
              <span className="detail-value">{selectedMigration?.app || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Environment:</span>
              <span className="detail-value">{selectedMigration?.env || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className={`detail-value status ${selectedMigration?.status?.toLowerCase() || 'unknown'}`}>
                {selectedMigration?.status
                  ? selectedMigration.status.charAt(0).toUpperCase() + selectedMigration.status.slice(1).toLowerCase()
                  : 'N/A'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date:</span>
              <span className="detail-value">{selectedMigration?.created_at ? new Date(selectedMigration.created_at).toLocaleString() : 'N/A'}</span>
            </div>
            {selectedMigration?.url && selectedMigration.url !== '---' && (
              <div className="detail-row">
                <span className="detail-label">Code Base URL:</span>
                <a 
                  href={selectedMigration.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-value url-link"
                >
                  {selectedMigration.url}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Chart Section */}
        <div className="chart-section">
          <h2 className="chart-title">Migration Analytics</h2>
          <div className="charts-container">
            {/* Lines of Code Chart */}
            <div className="chart-wrapper">
              <h3 className="chart-subtitle">Lines of Code</h3>
              <div className="donut-chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={linesOfCodeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={2}
                      dataKey="value"
                      label={renderPercentageLabel}
                      labelLine={false}
                    >
                      {linesOfCodeData.map((entry, index) => (
                        <Cell key={`loc-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number, name: string, props: any) => [
                        `${value.toLocaleString()} Lines (${props.payload.percentage.toFixed(2)}%)`,
                        name
                      ]}
                    />
                    <text 
                      x="50%" 
                      y="45%" 
                      textAnchor="middle" 
                      fill="#1f2937" 
                      fontSize="28" 
                      fontWeight="700"
                    >
                      {totalLines.toLocaleString()}
                    </text>
                    <text 
                      x="50%" 
                      y="55%" 
                      textAnchor="middle" 
                      fill="#6b7280" 
                      fontSize="16"
                    >
                      Lines
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Files by Type Chart */}
            <div className="chart-wrapper">
              <h3 className="chart-subtitle">Files by Type</h3>
              <div className="donut-chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={filesByTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={2}
                      dataKey="value"
                      label={renderPercentageLabel}
                      labelLine={false}
                    >
                      {filesByTypeData.map((entry, index) => (
                        <Cell key={`files-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number, name: string, props: any) => [
                        `${value} Files (${props.payload.percentage.toFixed(2)}%)`,
                        name
                      ]}
                    />
                    <text 
                      x="50%" 
                      y="45%" 
                      textAnchor="middle" 
                      fill="#1f2937" 
                      fontSize="28" 
                      fontWeight="700"
                    >
                      {totalFiles}
                    </text>
                    <text 
                      x="50%" 
                      y="55%" 
                      textAnchor="middle" 
                      fill="#6b7280" 
                      fontSize="16"
                    >
                      Files
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="chart-legend">
            {legendCategories.map((name, index) => (
              <div key={name} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: getCategoryColor(name, index) }}></div>
                <span className="legend-label">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Report;
